import clsx from 'clsx'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { type FC, useEffect, useState } from 'react'
import NextLink from 'next/link'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Shimmer } from '@wartek-id/shimmer'
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogCloseButton,
} from '@wartek-id/dialog-v1'
import { Link } from '@wartek-id/link'
import { Button } from '@wartek-id/button-v1'

import {
  CPKvKeys,
  useGetCPKeyPairValue,
} from 'api/content-platform/useAPIKeyValue'
import {
  useApiUserDetailOnboarding,
  useApiUserDetailProfile,
} from 'api/competency-mapping/useAPIUserDetail'
import { getUserDisplayName } from 'app/Homepage/utils/user'
import { useAuth } from 'utils/auth'
import ProfileHeaderBlend from 'app/PemetaanKompetensiPage/components/ProfileRevamp/ProfileHeaderBlend'
import { useAsesmenRefleksiPeriod } from 'app/PemetaanKompetensiPage/hooks/useAsesmenRefleksiPeriod'
import { CompetencyProfileCard } from 'app/PemetaanKompetensiPage/components/ProfileRevamp/CompetencyProfileCard'
import { useApiCompetencyProfile } from 'api/competency-mapping/useAPICompetencyProfile'
import { CompetencyIcons } from 'app/PemetaanKompetensiPage/constants/CompetencyIcons'
import { DEPARTEMENT_LEVEL_MAPPING } from 'app/PemetaanKompetensiPage/constants/competency'
import { useMediaQueryHelper } from 'app/PemetaanKompetensiPage/hooks/useMediaQueryHelper'
import { useSETrackEventRefkom } from '../../hooks/useSETrackEventRefkom'
import CompetencyCardAnimated from 'app/PemetaanKompetensiPage/components/ProfileRevamp/CompetencyCardAnimated'
import CompetencyCard from 'app/PemetaanKompetensiPage/components/ProfileRevamp/CompetencyCard'
import { useApiCompetencyMappingAssessmentStatus } from 'api/competency-mapping/useAPICompetencyMapping'
import { useExperimentsContext } from 'utils/providers/useExperimentsContext'

import type { ProfileCompetencyData } from 'types/competency-profile'
import type { IRPDToggleData } from 'types/content-platform'

import RpdIcon from 'assets/icons/profil-kompetensi/img-to-do-list-document.svg'

interface MainProps {
  currentDateTimeServer: string
}

const COMPETENCY_ROUTE = '/refleksi-kompetensi/'
const ASSESMENT_TYPE = 'REFLEKSI'

const Main: FC<MainProps> = ({ currentDateTimeServer }) => {
  const { session } = useAuth()
  const router = useRouter()
  const userDisplayName = getUserDisplayName(session?.user?.name)
  const { isDesktop } = useMediaQueryHelper()
  const [dialogCompetencyOpened, setDialogCompetencyOpened] =
    useState<string>(null)
  const {
    trackHomeRefleksiOpen,
    trackHomeRefleksiInactiveAssessmentOpen,
    trackClickTakeAssessment,
    trackClickAssessmentCard,
  } = useSETrackEventRefkom()

  const { isLoading: isLoadingUserProfile, data: userProfileData } =
    useApiUserDetailProfile(ASSESMENT_TYPE)

  const { deadlineDate } = useAsesmenRefleksiPeriod(currentDateTimeServer)

  const { getExperimentByName, isFetchExperimentsSuccess } =
    useExperimentsContext()
  // experiment animated icon
  const isVariant =
    getExperimentByName('refkom_adoption_page')?.treatment === 'variant'

  const isAnimatedIconVariantEnabled =
    process.env.NEXT_PUBLIC_REFKOM_EXPERIMENT_HOMEPAGE_ANIMATED_ENABLED ===
      'true' && isVariant

  const { isLoading: isLoadingAssessmentStatus, data: dataAssessmentStatus } =
    useApiCompetencyMappingAssessmentStatus(ASSESMENT_TYPE, {
      enabled: isAnimatedIconVariantEnabled,
    })

  const profileLevel = userProfileData?.data?.userConfirmation?.departmentLevel
  const currentFlow = userProfileData?.meta?.flow
  const minimumLevel = DEPARTEMENT_LEVEL_MAPPING[profileLevel]
  const onboardingType = 'REFLEKSI'

  const {
    isFetching: isFetchingCompetencyProfile,
    isSuccess: isSuccessCompetencyProfile,
    data: userCompetencyProfileData,
  } = useApiCompetencyProfile(ASSESMENT_TYPE, {
    enabled: !!profileLevel,
    initialData: {
      data: [
        { key: 'PEDAGOGIK', name: 'Pedagogik', level: 0 },
        { key: 'KEPRIBADIAN', name: 'Kepribadian', level: 0 },
        { key: 'SOSIAL', name: 'Sosial', level: 0 },
        { key: 'PROFESIONAL', name: 'Profesional', level: 0 },
      ],
      meta: {},
    },
  })

  const { isLoading, data: RpdEntryData } =
    useGetCPKeyPairValue<IRPDToggleData>(CPKvKeys.RPDToggle, true)

  const asesmenRefleksiPeriod = useAsesmenRefleksiPeriod(currentDateTimeServer)

  const {
    isLoading: isLoadingUserDetailOnboarding,
    isSuccess: isSuccessUserDetailOnboarding,
    data: UserDetailOnboarding,
  } = useApiUserDetailOnboarding(onboardingType)

  const handleClickAssesmentCard = (competencyData: ProfileCompetencyData) => {
    trackClickAssessmentCard({
      subCompetency: competencyData.name,
      variant: isAnimatedIconVariantEnabled ? 'variant' : 'control',
    })
    // a/b test target
    if (isAnimatedIconVariantEnabled && competencyData.level === 0) {
      router.push(
        `${COMPETENCY_ROUTE}/asesmen-refleksi/${competencyData.name.toLowerCase()}?source=refkom_home`
      )
      // normal route
    } else {
      if (competencyData.level == 0) {
        trackHomeRefleksiInactiveAssessmentOpen({
          subCompetency: competencyData.name.toLowerCase(),
        })
        setDialogCompetencyOpened(competencyData.name.toLowerCase())
      } else {
        router.push(
          `${COMPETENCY_ROUTE}/${competencyData.name.toLowerCase()}?source=refkom_home`
        )
      }
    }
  }

  useEffect(() => {
    if (isSuccessUserDetailOnboarding && isFetchExperimentsSuccess) {
      trackHomeRefleksiOpen(
        isAnimatedIconVariantEnabled ? 'variant' : 'control'
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUserDetailOnboarding, isFetchExperimentsSuccess])

  if (isLoading || isLoadingUserDetailOnboarding)
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    )

  const isDoneUserDetailOnboarding =
    !isLoadingUserDetailOnboarding && isSuccessUserDetailOnboarding

  if (isDoneUserDetailOnboarding)
    if (
      !UserDetailOnboarding?.data.find(
        (Onboarding) =>
          Onboarding.onboardingType === ASSESMENT_TYPE && Onboarding.onboarded
      )
    ) {
      // The conditional check above is to check if onboarding data of type 'REFLEKSI' exist and onboarded is true
      router.push('/refleksi-kompetensi/onboarding?source=refkom_home')
    }

  // if pedagogik & variant & not created at all
  const shouldDisplayAnimatedPedagogik = (competencyKey: string) =>
    competencyKey === 'PEDAGOGIK' &&
    isAnimatedIconVariantEnabled &&
    dataAssessmentStatus?.data?.every(
      (thisData) => thisData.status == 'NOT_CREATED'
    )

  return (
    <div className="flex flex-col">
      <div
        className={clsx(
          'flex bg-gradient-to-b from-[#FDFCFE] via-[#CFC3E2_31.2%] to-[#240872]',
          isDesktop ? 'h-[286px]' : 'h-[232px]'
        )}
      >
        <div className="bg-[#CFC3E2] mix-blend-multiply grow h-full"></div>
        <ProfileHeaderBlend
          className="w-full max-w-refkom"
          classNameBgContainer={clsx(
            'bg-cover',
            'bg-top',
            'mix-blend-multiply',
            isDesktop ? 'h-[286px]' : 'h-[232px]',
            isDesktop
              ? "bg-[url('/images/ilustrasi-profil-kompetensi.png')]"
              : "bg-[url('/images/ilustrasi-profil-kompetensi-mobile.png')]"
          )}
          classNameSubContainer={`max-w-refkom w-full absolute top-0 flex flex-col items-start justify-between ${
            isDesktop ? 'h-[286px]' : 'h-[232px]'
          } pb-4`}
        >
          <div
            className={clsx(
              isDesktop ? 'px-6' : 'px-4',
              'w-full flex flex-col'
            )}
          >
            <Text
              color="inverse"
              variant="heading-lg"
              className="truncate mb-2"
            >
              Halo, {userDisplayName}
            </Text>
            <Link
              variant="white"
              className="!p-0 mb-5"
              href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/21276699244953-Tentang-Refleksi-Kompetensi?focus=true"
              target="_blank"
            >
              Kenalan dengan Refleksi Kompetensi.
            </Link>
          </div>
        </ProfileHeaderBlend>
        <div className="bg-[#CFC3E2] mix-blend-multiply grow h-full" />
      </div>
      <div
        className={clsx(
          isDesktop ? 'pt-8 pb-10 px-6' : 'pt-5 pb-6 px-4',
          'max-w-refkom m-auto w-full'
        )}
      >
        {RpdEntryData?.data?.entryEnabled && (
          <div className="px-4 pb-5">
            <Text variant="heading-md" className="mb-4">
              Rencana Pengembangan Diri (RPD)
            </Text>
            <div
              className="rounded-lg flex flex-col border-subdued border p-3 bg-white"
              onClick={() =>
                router.push(`/pemetaan-kompetensi/rencana-pengembangan-diri`)
              }
            >
              <div className="flex flex-row items-center justify-between cursor-pointer">
                <div className="flex flex-row items-center gap-2.5">
                  <div className="h-12 w-12 self-start">
                    <RpdIcon />
                  </div>
                  <Text variant="body" className="capitalize">
                    Mari prioritaskan pembelajaran Anda sebelum{' '}
                    <Text variant="body-bold" as="span">
                      {deadlineDate}.
                    </Text>
                  </Text>
                </div>
                <Icon>chevron_right</Icon>
              </div>
            </div>
          </div>
        )}

        {!isAnimatedIconVariantEnabled && (
          <div className="pb-6">
            <div className="rounded-lg flex flex-col border-subdued border p-3 bg-white">
              <div className="relative aspect-[304/125] mb-3">
                <Image
                  src="/images/refleksi-kompetensi/img-asesmen-refleksi-entry.png"
                  layout="fill"
                  alt="ilustrasi asesmen refleksi"
                />
              </div>

              {asesmenRefleksiPeriod.isEntryOpen ? (
                <>
                  <Text variant="body">
                    Mulai refleksi dan dapatkan hasil capaian Anda sebelum{' '}
                    <Text variant="body-bold" as="span">
                      {deadlineDate}.
                    </Text>
                  </Text>
                  <NextLink href="/refleksi-kompetensi/asesmen-refleksi?source=refkom_home">
                    <Button className="mt-2">Mulai</Button>
                  </NextLink>
                </>
              ) : (
                <Text variant="body">
                  Mulai ikuti refleksi yang tersedia nanti. Cek kembali halaman
                  ini secara berkala, ya.
                </Text>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Text variant="heading-lg">Hasil refleksi Anda</Text>

          {isLoadingUserProfile ? (
            <Shimmer height="64px" width="100%" />
          ) : !!profileLevel ? (
            <CompetencyProfileCard level={profileLevel} flow={currentFlow} />
          ) : null}
          {isSuccessCompetencyProfile && !isLoadingAssessmentStatus && (
            <div className="space-y-3">
              {userCompetencyProfileData?.data.map((thisCompetencyData) => {
                if (shouldDisplayAnimatedPedagogik(thisCompetencyData.key)) {
                  return (
                    <CompetencyCardAnimated
                      isLoading={
                        isLoadingUserProfile || isFetchingCompetencyProfile
                      }
                      key={thisCompetencyData.key}
                      name={thisCompetencyData.name}
                      minimumLevel={minimumLevel}
                      level={thisCompetencyData.level}
                      onClick={() =>
                        handleClickAssesmentCard(thisCompetencyData)
                      }
                      isControl={!isAnimatedIconVariantEnabled}
                    />
                  )
                } else {
                  return (
                    <CompetencyCard
                      isLoading={
                        isLoadingUserProfile || isFetchingCompetencyProfile
                      }
                      key={thisCompetencyData.key}
                      name={thisCompetencyData.name}
                      minimumLevel={minimumLevel}
                      level={thisCompetencyData.level}
                      onClick={() =>
                        handleClickAssesmentCard(thisCompetencyData)
                      }
                      competencyData={dataAssessmentStatus?.data?.find(
                        (thisData) => thisData.label === thisCompetencyData.key
                      )}
                      isControl={!isAnimatedIconVariantEnabled}
                      icon={
                        CompetencyIcons[thisCompetencyData.key.toLowerCase()]
                      }
                    />
                  )
                }
              })}
            </div>
          )}
          <Dialog
            isOpen={dialogCompetencyOpened !== null}
            onClose={() => setDialogCompetencyOpened(null)}
            closeOnOverlayClick={false}
            autoFocus={false}
          >
            <DialogOverlay />
            <DialogContent className="max-w-2xl m-auto self-center rounded-lg">
              <DialogCloseButton style={{ top: '7px', right: '5px' }} />
              <div className="flex justify-center w-full max-h-[392px]">
                <img
                  src="/images/refleksi-kompetensi/img-refkom-mulai-prompt.jpg"
                  alt="onboarding image"
                  className="object-cover md:object-contain w-full h-auto rounded-t-lg -mt-1"
                  height={392}
                  width={560}
                />
              </div>
              <div className="px-4 pb-4">
                <Text variant="heading-sm" className="mt-4 mb-1">
                  Mulai refleksi terlebih dahulu, ya
                </Text>

                <Text variant="body">
                  Nanti hasil refleksinya akan kami tampilkan di sini.
                </Text>

                <Button
                  className="mt-4 mb-2"
                  color="blue"
                  onClick={() => {
                    trackClickTakeAssessment({
                      subCompetency: dialogCompetencyOpened,
                      variant: isAnimatedIconVariantEnabled
                        ? 'variant'
                        : 'control',
                    })
                    router.push(
                      '/refleksi-kompetensi/asesmen-refleksi?source=refkom_home'
                    )
                  }}
                  size="lg"
                  fullWidth
                >
                  Mulai
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
export default Main
